import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useLogin, useVerifyOtp, useResendOtp, completeLogin } from "../hooks/useAuth";
import { useAuthStore } from "../stores/authStore";
import { LoginForm } from "@/components/login-form";
import { OtpVerificationForm } from "../components/OtpVerificationForm";
import { ForcePasswordChangeDialog } from "../components/ForcePasswordChangeDialog";
import { toast } from "sonner";
import { getErrorMessage } from "@/shared/utils/errorHandler";
import { LoadingAnimation } from "@/shared/components/LoadingAnimation";
import type { OtpRequiredData } from "../services/authApi";

export const Login = () => {
  const [usernameOrEmail, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPasswordChangeDialog, setShowPasswordChangeDialog] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [otpData, setOtpData] = useState<OtpRequiredData | null>(null);
  const navigate = useNavigate();
  const loginMutation = useLogin();
  const verifyOtpMutation = useVerifyOtp();
  const resendOtpMutation = useResendOtp();
  const { setAuth } = useAuthStore();

  useEffect(() => {
    if (loginMutation.error) {
      toast.error(getErrorMessage(loginMutation.error));
    }
  }, [loginMutation.error]);

  useEffect(() => {
    if (verifyOtpMutation.error) {
      toast.error(getErrorMessage(verifyOtpMutation.error));
    }
  }, [verifyOtpMutation.error]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    loginMutation.mutate(
      { usernameOrEmail, password },
      {
        onSuccess: (data) => {
          if (data.requiresOtp && data.otpChallenge) {
            setOtpData(data.otpChallenge);
            setOtpStep(true);
          } else if (data.loginData) {
            completeLogin(data.loginData, setAuth);
            if (data.loginData.user.mustChangePassword) {
              setShowPasswordChangeDialog(true);
            } else {
              navigate("/dashboard");
            }
          }
        },
      },
    );
  };

  const handleVerifyOtp = (code: string) => {
    if (!otpData) return;
    verifyOtpMutation.mutate(
      { otpToken: otpData.otpToken, code },
      {
        onSuccess: (data) => {
          if (data.user.mustChangePassword) {
            setShowPasswordChangeDialog(true);
          } else {
            navigate("/dashboard");
          }
        },
      },
    );
  };

  const handleResendOtp = () => {
    if (!otpData) return;
    resendOtpMutation.mutate(
      { otpToken: otpData.otpToken },
      {
        onSuccess: (data) => {
          setOtpData({ ...otpData, expiresAt: data.expiresAt });
          toast.success("A new code has been sent to your phone.");
        },
      },
    );
  };

  const handleBackToLogin = () => {
    setOtpStep(false);
    setOtpData(null);
    verifyOtpMutation.reset();
  };

  const handlePasswordChangeSuccess = () => {
    setShowPasswordChangeDialog(false);
  };

  return (
    <div className="bg-muted flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
      {(loginMutation.isPending || verifyOtpMutation.isPending || resendOtpMutation.isPending) && (
        <LoadingAnimation
          fullScreen
          className="z-[100]"
          text={
            resendOtpMutation.isPending
              ? "Sending a new OTP…"
              : verifyOtpMutation.isPending
                ? "Verifying OTP…"
                : "Signing in and sending OTP…"
          }
        />
      )}
      <div className="w-full max-w-sm md:max-w-4xl">
        {otpStep && otpData ? (
          <OtpVerificationForm
            otpData={otpData}
            onVerify={handleVerifyOtp}
            onResend={handleResendOtp}
            onBack={handleBackToLogin}
            isVerifying={verifyOtpMutation.isPending}
            isResending={resendOtpMutation.isPending}
            error={verifyOtpMutation.error ? getErrorMessage(verifyOtpMutation.error) : null}
          />
        ) : (
          <LoginForm
            onSubmit={handleSubmit}
            email={usernameOrEmail}
            setEmail={setEmail}
            password={password}
            setPassword={setPassword}
            isLoading={loginMutation.isPending}
          />
        )}
      </div>

      <ForcePasswordChangeDialog
        isOpen={showPasswordChangeDialog}
        onSuccess={handlePasswordChangeSuccess}
      />
    </div>
  );
};
